import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Save, Globe, Phone, Mail, Image as ImageIcon } from 'lucide-react';
import Header from '../../components/Header';
import { useAuth } from '../../context/AuthContext';
import { venuesAPI, handleApiError } from '../../utils/api';

const EMPTY_FORM = {
  name: '',
  address: '',
  city: '',
  region: '',
  country: '',
  countryCode: '',
  postalCode: '',
  latitude: '',
  longitude: '',
  timezone: '',
  websiteUrl: '',
  phoneNumber: '',
  email: '',
  googleMapsUrl: '',
  imageUrl: '',
  description: '',
  isActive: true,
};

const CreateEditVenuePage = () => {
  const navigate = useNavigate();
  const { venueId } = useParams();
  const { userRole } = useAuth();
  const isEditMode = !!venueId;

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const loadVenue = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await venuesAPI.getById(venueId);
      const venue = response.data;
      setFormData({
        name: venue.name || '',
        address: venue.address || '',
        city: venue.city || '',
        region: venue.region || '',
        country: venue.country || '',
        countryCode: venue.countryCode || '',
        postalCode: venue.postalCode || '',
        latitude: venue.latitude ?? '',
        longitude: venue.longitude ?? '',
        timezone: venue.timezone || '',
        websiteUrl: venue.websiteUrl || '',
        phoneNumber: venue.phoneNumber || '',
        email: venue.email || '',
        googleMapsUrl: venue.googleMapsUrl || '',
        imageUrl: venue.imageUrl || '',
        description: venue.description || '',
        isActive: venue.isActive !== undefined ? venue.isActive : true,
      });
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setLoadError(`${errTitle} : ${details}`);
    } finally {
      setIsLoading(false);
    }
  }, [venueId]);

  useEffect(() => {
    if (isEditMode) {
      loadVenue();
    }
  }, [isEditMode, loadVenue]);

  // Vérification admin - doit être après les hooks
  if (userRole !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h2>
            <p className="text-gray-600 mb-6">Vous devez être administrateur pour accéder à cette page.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom du lieu est requis';
    }

    if (formData.latitude !== '' && isNaN(Number(formData.latitude))) {
      newErrors.latitude = 'La latitude doit être un nombre';
    }

    if (formData.longitude !== '' && isNaN(Number(formData.longitude))) {
      newErrors.longitude = 'La longitude doit être un nombre';
    }

    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Adresse email invalide';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        latitude: formData.latitude === '' ? null : Number(formData.latitude),
        longitude: formData.longitude === '' ? null : Number(formData.longitude),
      };

      if (isEditMode) {
        await venuesAPI.update(venueId, payload);
      } else {
        await venuesAPI.create(payload);
      }

      navigate('/admin/venues');
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setErrors((prev) => ({ ...prev, submit: `${errTitle} : ${details}` }));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 text-center text-gray-500">
          Chargement du lieu...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/admin/venues')}
            className="flex items-center text-gray-600 hover:text-blue-600 mb-6 transition"
          >
            <ArrowLeft size={20} className="mr-2" />
            Retour à la gestion des lieux
          </button>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
              <h1 className="text-3xl font-bold flex items-center">
                <MapPin className="mr-3" size={32} />
                {isEditMode ? 'Modifier le Lieu' : 'Créer un Nouveau Lieu'}
              </h1>
              <p className="mt-2 text-blue-100">
                {isEditMode ? 'Modifiez les informations de ce casino / salle de poker' : 'Remplissez les informations pour ajouter un lieu'}
              </p>
            </div>
          </div>

          {loadError && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
              {loadError}
            </div>
          )}

          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
              {errors.submit}
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-6">
            <div className="space-y-6">
              {/* Nom */}
              <div>
                <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                  <MapPin className="mr-2 text-blue-600" size={18} />
                  Nom du Lieu *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Ex: Casino Barrière de Deauville"
                  className={`w-full px-4 py-3 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              {/* Adresse */}
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">Adresse</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Ex: 2 Rue Edmond Blanc"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Ville / Région / Pays */}
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Ville</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Région</label>
                  <input
                    type="text"
                    name="region"
                    value={formData.region}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Pays</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Code pays / Code postal / Fuseau horaire */}
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Code pays</label>
                  <input
                    type="text"
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleChange}
                    placeholder="Ex: FR"
                    maxLength={2}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Code postal</label>
                  <input
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Fuseau horaire</label>
                  <input
                    type="text"
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleChange}
                    placeholder="Ex: Europe/Paris"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Latitude / Longitude */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Latitude</label>
                  <input
                    type="text"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleChange}
                    placeholder="Ex: 49.3567"
                    className={`w-full px-4 py-3 border ${errors.latitude ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {errors.latitude && <p className="text-red-500 text-sm mt-1">{errors.latitude}</p>}
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Longitude</label>
                  <input
                    type="text"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleChange}
                    placeholder="Ex: 0.0733"
                    className={`w-full px-4 py-3 border ${errors.longitude ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {errors.longitude && <p className="text-red-500 text-sm mt-1">{errors.longitude}</p>}
                </div>
              </div>

              {/* Site web / Google Maps */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                    <Globe className="mr-2 text-blue-600" size={18} />
                    Site web
                  </label>
                  <input
                    type="url"
                    name="websiteUrl"
                    value={formData.websiteUrl}
                    onChange={handleChange}
                    placeholder="https://exemple.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Lien Google Maps</label>
                  <input
                    type="url"
                    name="googleMapsUrl"
                    value={formData.googleMapsUrl}
                    onChange={handleChange}
                    placeholder="https://maps.google.com/..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Téléphone / Email */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                    <Phone className="mr-2 text-blue-600" size={18} />
                    Téléphone
                  </label>
                  <input
                    type="text"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                    <Mail className="mr-2 text-blue-600" size={18} />
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>
              </div>

              {/* Image URL */}
              <div>
                <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                  <ImageIcon className="mr-2 text-blue-600" size={18} />
                  URL de l'image (optionnel)
                </label>
                <input
                  type="url"
                  name="imageUrl"
                  value={formData.imageUrl}
                  onChange={handleChange}
                  placeholder="https://exemple.com/photo.jpg"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {formData.imageUrl && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600 mb-2">Aperçu :</p>
                    <img
                      src={formData.imageUrl}
                      alt="Aperçu"
                      className="w-32 h-32 object-cover border border-gray-300 rounded p-1"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Décrivez le lieu..."
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Actif */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="ml-3 text-sm font-semibold text-gray-700">
                    Lieu actif
                  </span>
                </label>
                <p className="text-xs text-gray-500 mt-2 ml-8">
                  Décochez pour masquer ce lieu sans le supprimer
                </p>
              </div>

              {/* Boutons */}
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => navigate('/admin/venues')}
                  className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
                  disabled={isSaving}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition flex items-center justify-center disabled:opacity-50"
                >
                  <Save className="mr-2 flex-shrink-0" size={20} />
                  {isSaving ? 'Enregistrement...' : isEditMode ? (
                    <>
                      <span className="sm:hidden">Enregistrer</span>
                      <span className="hidden sm:inline">Enregistrer les modifications</span>
                    </>
                  ) : 'Créer le lieu'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateEditVenuePage;
